import IncidentsSheet from "@/components/incidents-sheet"
import Button from "@/components/button"
import Card from "@/components/card"
import { FormulaDialog } from "@/components/formula-dialog"
import LinePicker, { LinePickerOption } from "@/components/line-picker"
import MachineRemarksSheet, { issues } from "@/components/machine-remarks-sheet"
import { ProductTabIndicator } from "@/components/product-tabs"
import RestartMachineWorkSheet from "@/components/restart-machine-work-sheet"
import { Icon } from "@/icons/icon"
import ParamRemarksSheet from "@/ipqc/components/param-remarks-sheet"
import RemarkDetailsSheet from "@/ipqc/components/remark-details-sheet"
import ResampleDetailsSheet from "@/ipqc/components/resample-details-sheet"
import { useFormEmit, UseFormReturn, useWatch } from "@/lib/form/form"
import { FormContextProvider, useFormContext } from "@/lib/form/form-context"
import { WidgetRegistry } from "@/lib/form/hooks"
import {
  AnySchema,
  FormSchema,
  InternalProcessSchema,
  MachineSchema,
  ParamSchema,
  ProductionLineSchema,
  ProductSchema,
} from "@/lib/form/schema"
import {
  BreakdownFormData,
  BreakdownType,
  FormStatus,
  isSubmittedLikeStatus,
} from "@/lib/form/types"
import { cn } from "@/lib/utils"
import useStore from "@/store"
import { toLineStatusKey } from "@/store/ipqc"
import { ComponentType } from "react"
import { format } from "date-fns"
import { useEffect, useState } from "react"

type ProcessStoreKey = "ipqc" | "boilout"
type LineStatusKeyFn = typeof toLineStatusKey

interface ProcessViewStore {
  setCurrLineIdx(index: number): void
  currLineIdx: number
  currProductIdx: number
  currHandoverSessionId: string
  currHandoverIdx: number
  filled: {
    self: Record<string, boolean>
    handovers: {
      filled: Record<string, boolean>
    }[]
  }
  lineStatus: Record<string, FormStatus | undefined>
  getCurrLineStatus(): FormStatus
}

export interface BaseFormViewProps {
  registry: WidgetRegistry
  formSchema: FormSchema
  path?: string
  processStoreKey?: ProcessStoreKey
  toLineStatusKeyFn?: LineStatusKeyFn
}

export interface FormViewProps extends BaseFormViewProps {
  className?: string
  schema: FormSchema
  activeProductId?: string
  form: UseFormReturn
  readOnly?: boolean
  sheetHasActiveBreakdown?: boolean
  ParamRemarksSheetComponent?: ComponentType<{ form: UseFormReturn }>
  RemarkDetailsSheetComponent?: ComponentType<{ form: UseFormReturn }>
  ResampleDetailsSheetComponent?: ComponentType<{ form: UseFormReturn }>
}

export function FormView({ schema, className, form, ...props }: FormViewProps) {
  const { productSchema, productIndex } = useActiveProductSchema()
  const processStoreKey = props.processStoreKey ?? "ipqc"
  const lineStatusKeyFn = props.toLineStatusKeyFn ?? toLineStatusKey
  const ParamRemarksSheetComponent =
    props.ParamRemarksSheetComponent ?? ParamRemarksSheet
  const RemarkDetailsSheetComponent =
    props.RemarkDetailsSheetComponent ?? RemarkDetailsSheet
  const ResampleDetailsSheetComponent =
    props.ResampleDetailsSheetComponent ?? ResampleDetailsSheet

  function useActiveProductSchema() {
    const [productIndex, setProductIndex] = useState(-1)

    useEffect(() => {
      const productIndex = schema.products.findIndex(
        (prod) => prod.id === props.activeProductId
      )

      setProductIndex(productIndex)
    }, [props.activeProductId, schema])

    return {
      productSchema: productIndex >= 0 ? schema.products[productIndex] : null,
      productIndex: productIndex,
    }
  }

  if (!schema.products.length) {
    return (
      <div className="pt-50 px-8 flex justify-center items-center">
        <h2 className="text-label-text">Please add products</h2>
      </div>
    )
  }

  if (!productSchema) {
    return false
  }

  return (
    <FormContextProvider
      form={form}
      registry={props.registry}
      formSchema={props.formSchema}
      readOnly={props.readOnly}
      additional={{
        sheetHasActiveBreakdown: props.sheetHasActiveBreakdown,
      }}
    >
      <form className={cn(className)}>
        <ProductFormView
          path={`products[${productIndex}]`}
          key={productSchema.id}
          schema={productSchema}
          {...props}
          processStoreKey={processStoreKey}
          toLineStatusKeyFn={lineStatusKeyFn}
        />
      </form>

      <MachineRemarksSheet form={form} />
      <RestartMachineWorkSheet form={form} processStoreKey={processStoreKey} />
      <ParamRemarksSheetComponent form={form} />
      <FormulaDialog form={form} />
      <IncidentsSheet form={form} />
      <RemarkDetailsSheetComponent form={form} />
      <ResampleDetailsSheetComponent form={form} />
    </FormContextProvider>
  )
}

interface ProductFormViewProps extends BaseFormViewProps {
  schema: ProductSchema
}

function ProductFormView({ schema, path, ...props }: ProductFormViewProps) {
  const processStore = useStore(
    (state) => state[props.processStoreKey ?? "ipqc"] as ProcessViewStore
  )
  const lines = useLines()
  const setCurrLineIndex = processStore.setCurrLineIdx
  const currLineIndex = processStore.currLineIdx
  const currProdIdx = processStore.currProductIdx
  const currHandoverSessionId = processStore.currHandoverSessionId
  const currHandoverIdx = processStore.currHandoverIdx
  const currLine = useCurrentLine()
  const filled = processStore.filled
  const lineStatus = processStore.lineStatus

  const lineIndicators = useLineIndicators()

  function useLines() {
    const [lines, setLines] = useState<LinePickerOption[]>([])

    useEffect(() => {
      setLines(
        schema.productionLines.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      )
    }, [schema])

    return lines
  }

  function useCurrentLine() {
    const [currLine, setCurrLine] = useState<ProductionLineSchema | null>(
      schema.productionLines[0] || null
    )

    useEffect(() => {
      if (currLineIndex >= 0) {
        setCurrLine(schema.productionLines[currLineIndex])
      } else {
        setCurrLine(null)
      }
    }, [currLineIndex, currProdIdx, schema])

    return currLine
  }

  function useLineIndicators(): ProductTabIndicator[] {
    const [indicators, setIndicators] = useState<ProductTabIndicator[]>([])

    useEffect(() => {
      if (currHandoverSessionId) {
        const newIndicators = schema.productionLines.map((line, idx) => {
          const filledKey = `${currProdIdx}-${idx}`
          const lineStatusKey = (props.toLineStatusKeyFn ?? toLineStatusKey)({
            productId: schema.id,
            lineId: line.id,
            sessionId: currHandoverSessionId,
          })

          if (isSubmittedLikeStatus(lineStatus[lineStatusKey])) {
            return ProductTabIndicator.Success
          }

          if (filled.handovers[currHandoverIdx].filled[filledKey]) {
            return ProductTabIndicator.Warning
          }

          return ProductTabIndicator.None
        })
        setIndicators(newIndicators)
      } else {
        const newIndicators = schema.productionLines.map((line, idx) => {
          const filledKey = `${currProdIdx}-${idx}`
          const lineStatusKey = (props.toLineStatusKeyFn ?? toLineStatusKey)({
            productId: schema.id,
            lineId: line.id,
            sessionId: "self",
          })

          if (isSubmittedLikeStatus(lineStatus[lineStatusKey])) {
            return ProductTabIndicator.Success
          }

          if (filled.self[filledKey]) {
            return ProductTabIndicator.Warning
          }

          return ProductTabIndicator.None
        })
        setIndicators(newIndicators)
      }
    }, [
      currHandoverIdx,
      currHandoverSessionId,
      currProdIdx,
      filled,
      lineStatus,
      lines,
      props.toLineStatusKeyFn,
      schema,
    ])

    return indicators
  }

  function handleChangeLine(selected: string) {
    if (!schema.productionLines.length) {
      return
    }

    const found = schema.productionLines.findIndex(
      (item) => item.id === selected
    )

    if (found === -1) {
      setCurrLineIndex(0)
    } else {
      setCurrLineIndex(found)
    }
  }

  return (
    <div>
      <section>
        <LinePicker
          options={lines}
          value={lines[currLineIndex]?.value}
          indicators={lineIndicators}
          onChange={handleChangeLine}
        />
      </section>

      <section className="mt-5">
        {!!currLine && (
          <LineFormView
            schema={currLine}
            path={`${path}.productionLines[${currLineIndex}]`}
            {...props}
          />
        )}
      </section>
    </div>
  )
}

interface LineFormViewProps extends BaseFormViewProps {
  schema: ProductionLineSchema
}

function LineFormView({ schema, path = "", ...props }: LineFormViewProps) {
  return (
    <section className="flex flex-col gap-4">
      {schema.machines.map((stage, index) => (
        <StageFormView
          key={stage.id}
          path={`${path}.machines[${index}]`}
          schema={stage}
          {...props}
        />
      ))}
    </section>
  )
}

interface StageFormViewProps extends BaseFormViewProps {
  schema: MachineSchema
}

function StageFormView({ schema, path = "", ...props }: StageFormViewProps) {
  const { form, readOnly: ctxReadOnly } = useFormContext()
  const emit = useFormEmit(form)
  const lineStatus = useStore(
    (state) =>
      (state[props.processStoreKey ?? "ipqc"] as ProcessViewStore).getCurrLineStatus()
  )
  const breakdowns = useWatch({
    form,
    name: `${path}.breakdown`,
    defaultValue: [],
  })

  const isCurrentlyInBreakdown =
    breakdowns.length && !breakdowns.at(-1)?.endedAt

  function handleClickRemarks() {
    if (isCurrentlyInBreakdown) {
      emit("restartMachineWork", {
        path,
        schema,
      })
    } else {
      emit("clickMachineRemarks", {
        path,
        schema,
      })
    }
  }

  return (
    <Card className="px-0">
      <div className="flex justify-between items-center px-3">
        <h3 className="text-button-black font-poppins text-lg font-semibold">
          {schema.name}
        </h3>

        {!isSubmittedLikeStatus(lineStatus) && !ctxReadOnly && (
          <button
            className="hover:cursor-pointer"
            type="button"
            onClick={handleClickRemarks}
          >
            {isCurrentlyInBreakdown ? (
              <Icon name="RedFlag" className="h-4 w-auto" />
            ) : (
              <Icon name="FlagNeutral" className="h-4 w-auto" />
            )}
          </button>
        )}
      </div>

      <div className="flex flex-col gap-6 mt-4">
        {schema.internalProcesses.map((process, index) => (
          <div key={process.id}>
            {schema.internalProcesses.length > 1 && (
              <h4
                className="
                  uppercase px-4 py-2.5 font-semibold text-sm
                  bg-band-fill
                "
              >
                {process.name}
              </h4>
            )}

            <ProcessFormView
              className="mt-4 px-3"
              path={`${path}.internalProcesses[${index}]`}
              schema={process}
              {...props}
            />
          </div>
        ))}
      </div>

      <BreakdownSummary
        className="px-3"
        status={lineStatus}
        schema={schema}
        form={form}
        path={path}
        breakdowns={breakdowns}
      />
    </Card>
  )
}

export interface BreakdownSummaryProps {
  className?: string
  schema: AnySchema
  form: UseFormReturn
  path: string
  breakdowns: BreakdownFormData[]
  status: FormStatus
}

export function BreakdownSummary({
  className,
  status,
  schema,
  form,
  path,
  breakdowns,
}: BreakdownSummaryProps) {
  const emit = useFormEmit(form)

  function formatTime(date: string) {
    try {
      const obj = new Date(date)

      return format(obj, "HH:mm")
    } catch {
      return ""
    }
  }

  function formatBreakdownText() {
    const lastBreakdown = breakdowns.at(-1)

    if (breakdowns.length === 1 || !lastBreakdown?.endedAt) {
      const breakdown = breakdowns.at(-1)

      if (!breakdown) {
        return "No breakdown found"
      }

      const label =
        issues.find((issue) => issue.value === breakdown.type)?.label ||
        "Incident"

      if (breakdown.endedAt) {
        return `${label} reported from ${formatTime(breakdown.startedAt)} to ${formatTime(breakdown.endedAt)}`
      } else {
        return `${label} at ${formatTime(breakdown.startedAt)}`
      }
    } else {
      return `${breakdowns.length} incidents reported`
    }
  }

  function showRestart(): boolean {
    if (isSubmittedLikeStatus(status)) {
      return false
    }

    const lastBreakdown = breakdowns.at(-1)
    if (
      !lastBreakdown?.endedAt &&
      lastBreakdown?.type !== BreakdownType.CHANGE_OVER
    ) {
      return true
    }

    return false
  }

  function handleRestart() {
    emit("restartMachineWork", {
      path,
      schema,
    })
  }

  function handleClickSummary() {
    emit("showBreakdownList", {
      schema,
      path,
    })
  }

  if (!breakdowns.length) {
    return false
  }

  return (
    <div className={cn("flex items-center gap-1 mt-4", className)}>
      <button
        className="
          border border-error py-1.5 px-2 text-error
          text-sm font-medium grow flex gap-2 items-center
          justify-between h-8 font-roboto hover:cursor-pointer
        "
        type="button"
        onClick={handleClickSummary}
      >
        <span>{formatBreakdownText()}</span>
        <Icon name="RedFlag" />
      </button>

      {showRestart() && (
        <Button size="sm" onClick={handleRestart}>
          Restart
        </Button>
      )}
    </div>
  )
}

interface ProcessFormViewProps extends BaseFormViewProps {
  className?: string
  schema: InternalProcessSchema
}

function ProcessFormView({
  className,
  schema,
  path = "",
  ...props
}: ProcessFormViewProps) {
  return (
    <section
      className={cn(
        `
          flex flex-col gap-4
        `,
        className
      )}
    >
      {schema.parameters.map((param, index) => (
        <ParamFormView
          key={param.id}
          path={`${path}.parameters[${index}]`}
          schema={param}
          {...props}
        />
      ))}
    </section>
  )
}

interface ParamFormViewProps extends BaseFormViewProps {
  schema: ParamSchema
}

function ParamFormView({
  schema,
  registry,
  path = "",
  formSchema,
}: ParamFormViewProps) {
  const Component = registry.get(schema.type)

  if (!Component) {
    return false
  }

  return <Component schema={schema} path={path} formSchema={formSchema} />
}
